import React, { useEffect, useRef, useState, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import MainLayout from '../components/MainLayout';
import PrintableReceipt from '../components/PrintableReceipt';
import { useOrderNotifications } from '../hooks/useOrderNotifications';

const ORDER_COLUMNS = [
    { 
        id: 'incoming', 
        title: 'Incoming / Active', 
        subtitle: 'Needs Attention', 
        bg: 'bg-amber-50 dark:bg-amber-900/10', 
        border: 'border-2 border-amber-400', 
        statuses: ['pending'],
        emptyIcon: 'local_cafe',
        emptyText: 'Kitchen is clear. Waiting for orders...'
    },
    { 
        id: 'progress', 
        title: 'In Progress', 
        subtitle: 'Being Prepared', 
        bg: 'bg-emerald-50 dark:bg-emerald-900/10', 
        border: 'border border-emerald-500', 
        statuses: ['preparing'],
        emptyIcon: 'skillet',
        emptyText: 'Nothing cooking right now.'
    },
    { 
        id: 'completed', 
        title: 'Completed', 
        subtitle: 'Ready / Served', 
        bg: 'bg-slate-50/50 dark:bg-slate-900/50', 
        border: 'border border-slate-200 dark:border-slate-800', 
        statuses: ['ready', 'completed'],
        emptyIcon: 'task_alt',
        emptyText: 'All caught up! Great job.'
    }
];

// Helper to calculate elapsed time in minutes using explicitly passed 'now' state
const getElapsedMinutes = (receivedAt, nowTime) => {
    if (!receivedAt || !nowTime) return 0;
    const diff = nowTime - new Date(receivedAt);
    return Math.max(0, Math.floor(diff / 60000));
};

export default function OrdersPage() {
    const [orders, setOrders] = useState([]);
    const [restaurant, setRestaurant] = useState(null);
    const [restaurantId, setRestaurantId] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // UI State
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [printingOrder, setPrintingOrder] = useState(null);
    const [soundEnabled, setSoundEnabled] = useState(true);

    const [now, setNow] = useState(new Date());

    const [newOrderIds, setNewOrderIds] = useState(new Set());
    const seenIdsRef = useRef(new Set());
    const { playBeep, fireNotification } = useOrderNotifications(soundEnabled);

    // Timer tick every minute to update elapsed times
    useEffect(() => {
        const interval = setInterval(() => setNow(new Date()), 60000);
        const handleKeyDown = (e) => { if (e.key === 'Escape') setSelectedOrder(null); };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            clearInterval(interval);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    // Print logic
    useEffect(() => {
        if (printingOrder) {
            setTimeout(() => { window.print(); }, 100);
        }
    }, [printingOrder]);

    useEffect(() => {
        const handleAfterPrint = () => setPrintingOrder(null);
        window.addEventListener('afterprint', handleAfterPrint);
        return () => window.removeEventListener('afterprint', handleAfterPrint);
    }, []);

    // Fetch data
    const restaurantIdRef = useRef(null);

    const fetchAllOrders = async (restId) => {
        const id = restId || restaurantIdRef.current;
        if (!id) return;
        const { data: ordersData } = await supabase
            .from('orders')
            .select('*, order_items (*)')
            .eq('restaurant_id', id)
            .order('created_at', { ascending: false });

        const validOrders = (ordersData || []).filter(o => o.payment_status !== 'awaiting_payment');

        setOrders(prev => {
            const nowTime = new Date();
            const newOnes = validOrders.filter(o => !seenIdsRef.current.has(o.id));
            
            // Map orders and preserve/add receivedAt
            const updatedOrders = validOrders.map(o => {
                const existing = prev.find(p => p.id === o.id);
                if (existing?.receivedAt) return { ...o, receivedAt: existing.receivedAt };
                
                // If it's a "new" order being seen for the first time in this session
                if (!seenIdsRef.current.has(o.id)) {
                    return { ...o, receivedAt: nowTime };
                }
                
                // Fallback for existing orders on initial load
                return { ...o, receivedAt: new Date(o.created_at) };
            });

            if (newOnes.length > 0) {
                newOnes.forEach(o => {
                    seenIdsRef.current.add(o.id);
                    if (soundEnabled) playBeepRef.current?.();
                    fireNotificationRef.current?.(o);
                    setNewOrderIds(ids => {
                        const next = new Set([...ids, o.id]);
                        setTimeout(() => setNewOrderIds(s => { const n = new Set(s); n.delete(o.id); return n; }), 8000);
                        return next;
                    });
                });
            }
            return updatedOrders;
        });
    };

    useEffect(() => {
        async function init() {
            try {
                const { data: { user } } = await supabase.auth.getUser();
                if (!user) return;

                const { data: rest } = await supabase
                    .from('restaurants')
                    .select('*')
                    .eq('owner_id', user.id)
                    .single();

                if (!rest) return;
                setRestaurant(rest);
                setRestaurantId(rest.id);
                restaurantIdRef.current = rest.id;

                await fetchAllOrders(rest.id);
            } catch (err) {
                console.error('OrdersPage init error:', err);
            } finally {
                setLoading(false);
            }
        }
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const playBeepRef = useRef(playBeep);
    const fireNotificationRef = useRef(fireNotification);
    useEffect(() => { playBeepRef.current = playBeep; }, [playBeep]);
    useEffect(() => { fireNotificationRef.current = fireNotification; }, [fireNotification]);

    // Realtime subscription
    useEffect(() => {
        if (!restaurantId) return;
        const channel = supabase
            .channel(`orders-rt-${restaurantId}`)
            .on('postgres_changes',
                { event: '*', schema: 'public', table: 'orders', filter: `restaurant_id=eq.${restaurantId}` },
                () => { fetchAllOrders(); }
            )
            .subscribe();
        return () => { supabase.removeChannel(channel); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [restaurantId]);

    // Actions
    const updateOrderStatus = async (orderId, newStatus) => {
        const originalStatus = orders.find(o => o.id === orderId)?.status;
        try {
            setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
            if (selectedOrder?.id === orderId) {
                setSelectedOrder(prev => ({ ...prev, status: newStatus }));
            }
            const { error } = await supabase.from('orders').update({ status: newStatus }).eq('id', orderId);
            if (error) throw error;
        } catch (err) {
            console.error("Status update failed:", err);
            setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: originalStatus } : o));
        }
    };

    const markAllCompleted = async () => {
        const pendingOrPreparing = orders.filter(o => ['pending', 'preparing'].includes(o.status));
        for (const order of pendingOrPreparing) {
            await updateOrderStatus(order.id, 'completed');
        }
    };

    // Memoized Stats
    const stats = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const incoming = orders.filter(o => o.status === 'pending');
        const completed = orders.filter(o => (o.status === 'completed' || o.status === 'ready'));
        const cancelled = orders.filter(o => o.status === 'cancelled');
        
        // Stats for "Today" only
        const completedToday = completed.filter(o => new Date(o.created_at) >= today);
        const cancelledToday = cancelled.filter(o => new Date(o.created_at) >= today);
        const revenueToday = completedToday.reduce((sum, o) => sum + Number(o.total_amount || 0), 0);

        // Dynamic Avg Prep Time Calculation (All time or last 24h?)
        const completedWithTime = orders.filter(o => o.status === 'completed' && o.updated_at);
        const avgPrepTime = completedWithTime.length > 0 
            ? Math.round(completedWithTime.reduce((sum, o) => {
                const diff = (new Date(o.updated_at) - new Date(o.created_at)) / 60000;
                return sum + Math.max(0, diff);
              }, 0) / completedWithTime.length)
            : 0;

        return {
            incoming: incoming.length,
            completed: completedToday.length,
            cancelled: cancelledToday.length,
            avgPrepTime: avgPrepTime || 12,
            revenue: revenueToday
        };
    }, [orders]);

    // Pre-calculate grouped and sorted orders to prevent render-blocking lag
    const groupedOrders = useMemo(() => {
        const grouped = {};
        ORDER_COLUMNS.forEach(col => {
            grouped[col.id] = orders
                .filter(o => col.statuses.includes(o.status))
                .sort((a,b) => new Date(b.created_at) - new Date(a.created_at));
        });
        return grouped;
    }, [orders]);

    // Render components
    const renderTimer = (order) => {
        const mins = getElapsedMinutes(order.receivedAt || order.created_at, now);
        let color = "text-[#16A34A] dark:text-emerald-400";
        if (mins >= 15) color = "text-[#F59E0B] dark:text-amber-400";
        if (mins >= 30) color = "text-[#DC2626] dark:text-red-400";
        return <span className={`font-bold flex items-center gap-1 ${color}`}><span className="material-symbols-outlined text-[16px]">timer</span> {mins}m</span>;
    };

    const renderBadge = (type, val) => {
        if (type === 'payment_method') {
            return (
                <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-[#ECFDF5] text-[#065F46] dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                    {val === 'online' ? 'Online' : 'Cash'}
                </span>
            );
        }
        if (type === 'payment_status') {
            const isPaid = val === 'paid';
            return (
                <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider border transition-colors ${isPaid ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`}>
                    {isPaid ? 'Paid' : 'Unpaid'}
                </span>
            );
        }
    };

    return (
        <MainLayout activeLink="Orders" title="Orders Dashboard">
            <div className="flex flex-1 overflow-hidden h-full relative">
                
                {/* Main Content Area */}
                <div className="flex-1 flex flex-col bg-[#F8FAFC] dark:bg-slate-950 p-4 lg:p-6 overflow-y-auto no-print">
                    
                    {/* Top Bar */}
                    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6 shrink-0">
                        <div className="flex items-center gap-3">
                            <button 
                                onClick={() => fetchAllOrders()} 
                                aria-label="Refresh Orders"
                                className="flex items-center justify-center w-10 h-10 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-slate-600 dark:text-slate-300 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                            >
                                <span className="material-symbols-outlined">refresh</span>
                            </button>
                            <button 
                                onClick={markAllCompleted} 
                                className="bg-slate-900 hover:bg-slate-800 dark:bg-slate-800 dark:hover:bg-slate-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold shadow-sm transition-colors active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                            >
                                Mark All Completed
                            </button>
                            <div className="bg-white dark:bg-slate-900 px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-3">
                                <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider hidden sm:inline">Revenue Today</span>
                                <span className="text-lg font-bold text-slate-900 dark:text-white tabular-nums">₹{stats.revenue}</span>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <button 
                                onClick={() => setSoundEnabled(!soundEnabled)} 
                                aria-label={soundEnabled ? "Disable sound" : "Enable sound"}
                                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary active:scale-95 ${soundEnabled ? 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 shadow-sm' : 'bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-400'}`}
                            >
                                <span className="material-symbols-outlined text-[18px]">{soundEnabled ? 'volume_up' : 'volume_off'}</span>
                                <span className="hidden sm:inline">{soundEnabled ? 'Sound On' : 'Sound Off'}</span>
                            </button>

                        </div>
                    </div>

                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-6 shrink-0">
                        <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-xl border border-amber-100 dark:border-amber-800/50 flex flex-col justify-center">
                            <h3 className="text-xs lg:text-sm font-medium text-amber-800 dark:text-amber-500 mb-1">Incoming / Active</h3>
                            <span className="text-2xl lg:text-3xl font-bold text-amber-900 dark:text-amber-400 tabular-nums">{stats.incoming}</span>
                        </div>
                        <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-xl border border-emerald-100 dark:border-emerald-800/50 flex flex-col justify-center">
                            <h3 className="text-xs lg:text-sm font-medium text-emerald-800 dark:text-emerald-500 mb-1">Completed Today</h3>
                            <span className="text-2xl lg:text-3xl font-bold text-emerald-900 dark:text-emerald-400 tabular-nums">{stats.completed}</span>
                        </div>
                        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-xl border border-red-100 dark:border-red-800/50 flex flex-col justify-center">
                            <h3 className="text-xs lg:text-sm font-medium text-red-800 dark:text-red-500 mb-1">Cancelled Today</h3>
                            <span className="text-2xl lg:text-3xl font-bold text-red-900 dark:text-red-400 tabular-nums">{stats.cancelled}</span>
                        </div>
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-100 dark:border-blue-800/50 flex flex-col justify-center">
                            <h3 className="text-xs lg:text-sm font-medium text-blue-800 dark:text-blue-500 mb-1">Avg Prep Time</h3>
                            <span className="text-2xl lg:text-3xl font-bold text-blue-900 dark:text-blue-400 tabular-nums">{stats.avgPrepTime}m</span>
                        </div>
                    </div>

                    {/* Orders Columns */}
                    {loading ? (
                        <div className="flex-1 flex items-center justify-center">
                            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : (
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 overflow-hidden">
                            {ORDER_COLUMNS.map(col => {
                                const columnOrders = groupedOrders[col.id] || [];
                                return (
                                    <div 
                                        key={col.id} 
                                        className={`flex flex-col rounded-2xl ${col.bg} ${col.border} overflow-hidden transition-colors`}
                                        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('ring-4', 'ring-primary/50'); }}
                                        onDragLeave={(e) => e.currentTarget.classList.remove('ring-4', 'ring-primary/50')}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            e.currentTarget.classList.remove('ring-4', 'ring-primary/50');
                                            const orderId = e.dataTransfer.getData('orderId');
                                            if (orderId) updateOrderStatus(orderId, col.statuses[0]);
                                        }}
                                    >
                                        <div className="p-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
                                            <div className="flex justify-between items-center mb-1">
                                                <h2 className="font-bold text-slate-900 dark:text-slate-100">{col.title}</h2>
                                                <span className="px-2.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded-full text-xs font-bold text-slate-600 dark:text-slate-400">{columnOrders.length}</span>
                                            </div>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">{col.subtitle}</p>
                                        </div>
                                        
                                        <div className="flex-1 overflow-y-auto p-3 space-y-3">
                                            {columnOrders.map(order => {
                                                const isSelected = selectedOrder?.id === order.id;
                                                const isNew = newOrderIds.has(order.id);
                                                
                                                return (
                                                    <div 
                                                        key={order.id} 
                                                        draggable="true"
                                                        onDragStart={(e) => {
                                                            e.dataTransfer.setData('orderId', order.id);
                                                            e.dataTransfer.effectAllowed = "move";
                                                        }}
                                                        className={`relative bg-[#FFFFFF] dark:bg-slate-900 rounded-[16px] p-4 shadow-[0_6px_16px_rgba(0,0,0,0.06)] dark:shadow-black/20 transition-all hover:-translate-y-0.5 hover:shadow-lg focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 dark:focus-within:ring-offset-slate-900 cursor-grab active:cursor-grabbing ${isSelected ? 'ring-2 ring-primary' : 'border border-transparent dark:border-slate-800'} ${isNew ? 'animate-pulse ring-4 ring-amber-500 shadow-xl shadow-amber-500/20' : ''}`}
                                                    >
                                                        {/* Hidden accessible button that covers the entire card */}
                                                        <button
                                                            onClick={() => setSelectedOrder(order)}
                                                            className="absolute inset-0 w-full h-full rounded-[16px] z-0 focus:outline-none"
                                                            aria-label={`View details for order ${order.id.slice(0, 5)}`}
                                                        />
                                                        
                                                        <div className="relative z-10 pointer-events-none flex justify-between items-start mb-3">
                                                            <div>
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <span className="text-sm font-bold text-slate-900 dark:text-white">#{order.id.slice(0, 5).toUpperCase()}</span>
                                                                    {renderTimer(order)}
                                                                </div>
                                                                <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                                                    {order.table_number ? `Table ${order.table_number}` : 'Takeaway'} • {order.customer_name || 'Guest'}
                                                                </div>
                                                            </div>
                                                            <div className="text-base font-bold text-slate-900 dark:text-white">
                                                                ₹{order.total_amount}
                                                            </div>
                                                        </div>

                                                        {/* Items summary */}
                                                        <div className="relative z-10 pointer-events-none mb-4">
                                                            <p className="text-xs font-medium text-[#6B7280] dark:text-slate-400 line-clamp-2">
                                                                {order.order_items?.map(i => `${i.quantity}x ${i.name}`).join(', ')}
                                                            </p>
                                                        </div>

                                                        {/* Badges */}
                                                        <div className="relative z-10 pointer-events-none flex gap-2 mb-4">
                                                            {renderBadge('payment_method', order.payment_method)}
                                                            {renderBadge('payment_status', order.payment_status)}
                                                        </div>

                                                        {/* Actions */}
                                                        <div className="relative z-10 space-y-2">
                                                            {col.id === 'incoming' && (
                                                                <button 
                                                                    onClick={(e) => { e.stopPropagation(); updateOrderStatus(order.id, 'preparing'); }}
                                                                    className="w-full h-[44px] bg-[#16A34A] hover:bg-[#15803d] text-white font-semibold rounded-[10px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#16A34A] focus-visible:ring-offset-2 active:scale-95"
                                                                >
                                                                    Start Preparing
                                                                </button>
                                                            )}
                                                            {col.id === 'progress' && (
                                                                <button 
                                                                    onClick={(e) => { e.stopPropagation(); updateOrderStatus(order.id, 'ready'); }}
                                                                    className="w-full h-[44px] bg-[#16A34A] hover:bg-[#15803d] text-white font-semibold rounded-[10px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#16A34A] focus-visible:ring-offset-2 active:scale-95"
                                                                >
                                                                    Mark as Ready
                                                                </button>
                                                            )}
                                                            {col.id === 'completed' && order.status === 'ready' && (
                                                                <button 
                                                                    onClick={(e) => { e.stopPropagation(); updateOrderStatus(order.id, 'completed'); }}
                                                                    className="w-full h-[44px] bg-slate-900 dark:bg-slate-800 hover:bg-slate-800 text-white font-semibold rounded-[10px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 active:scale-95"
                                                                >
                                                                    Mark as Served
                                                                </button>
                                                            )}

                                                            {order.payment_status !== 'paid' && order.payment_method === 'cash' && (
                                                                <button 
                                                                    onClick={async (e) => { 
                                                                        e.stopPropagation(); 
                                                                        const { error } = await supabase.from('orders').update({ payment_status: 'paid' }).eq('id', order.id);
                                                                        if (!error) fetchAllOrders();
                                                                    }}
                                                                    className="w-full h-[44px] bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-[10px] transition-colors shadow-sm active:scale-95"
                                                                >
                                                                    Mark as Paid
                                                                </button>
                                                            )}
                                                            
                                                            <div className="flex gap-2">
                                                                {col.id !== 'completed' && (
                                                                    <button 
                                                                        onClick={(e) => { e.stopPropagation(); updateOrderStatus(order.id, 'cancelled'); }}
                                                                        className="flex-1 h-9 bg-[#FEE2E2] dark:bg-red-900/30 text-red-700 dark:text-red-400 font-medium rounded-lg text-xs transition-colors hover:bg-red-200 dark:hover:bg-red-900/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 active:scale-95"
                                                                    >
                                                                        Reject
                                                                    </button>
                                                                )}
                                                                <button 
                                                                    onClick={(e) => { e.stopPropagation(); setPrintingOrder(order); }}
                                                                    className="flex-1 h-9 bg-[#E5E7EB] dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium rounded-lg text-xs transition-colors hover:bg-slate-300 dark:hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 active:scale-95"
                                                                >
                                                                    Print
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {columnOrders.length === 0 && (
                                                <div className="flex flex-col items-center justify-center h-48 text-slate-400 dark:text-slate-500 text-center px-4">
                                                    <span className="material-symbols-outlined text-4xl mb-3 opacity-40">{col.emptyIcon}</span>
                                                    <span className="text-sm font-semibold opacity-80">{col.emptyText}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}



                </div>

                {/* Right Panel Details (Slide over on mobile/tablet, fixed width on xl desktop) */}
                <div 
                    className={`
                        fixed inset-y-0 right-0 z-50 w-full sm:w-[340px] bg-[#FFFFFF] dark:bg-slate-900 border-l border-[#E5E7EB] dark:border-slate-800 flex flex-col h-full no-print
                        transform transition-transform duration-300 ease-in-out shadow-2xl xl:shadow-none
                        xl:relative xl:translate-x-0
                        ${selectedOrder ? 'translate-x-0' : 'translate-x-full'}
                    `}
                >
                    {selectedOrder ? (
                        <>
                            <div className="p-5 border-b border-[#E5E7EB] dark:border-slate-800 flex justify-between items-center shrink-0">
                                <h2 className="font-bold text-slate-900 dark:text-white">Order Details</h2>
                                <button 
                                    onClick={() => setSelectedOrder(null)} 
                                    aria-label="Close order details"
                                    className="p-2 -mr-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                                >
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>
                            
                            <div className="flex-1 overflow-y-auto p-5 space-y-6">
                                {/* Header Info */}
                                <div>
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="text-2xl font-bold text-slate-900 dark:text-white">#{selectedOrder.id.slice(0, 5).toUpperCase()}</h3>
                                        {renderTimer(selectedOrder)}
                                    </div>
                                    <p className="text-sm text-slate-500 dark:text-slate-400">
                                        {selectedOrder.customer_name || 'Guest'} • {selectedOrder.table_number ? `Table ${selectedOrder.table_number}` : 'Takeaway'}
                                    </p>
                                </div>

                                {/* Status Badges */}
                                <div className="flex flex-wrap gap-2">
                                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                                        Status: {selectedOrder.status}
                                    </span>
                                    {renderBadge('payment_method', selectedOrder.payment_method)}
                                    {renderBadge('payment_status', selectedOrder.payment_status)}
                                </div>

                                {/* Items List */}
                                <div>
                                    <h4 className="text-sm font-bold text-slate-900 dark:text-white mb-3">Items</h4>
                                    <div className="space-y-3">
                                        {selectedOrder.order_items?.map((item, i) => (
                                            <div key={i} className="flex justify-between items-start">
                                                <div className="flex gap-3">
                                                    <span className="font-bold text-slate-900 dark:text-white">{item.quantity}x</span>
                                                    <div>
                                                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{item.name}</p>
                                                    </div>
                                                </div>
                                                <span className="text-sm font-bold text-slate-900 dark:text-white tabular-nums">₹{(item.price * item.quantity).toFixed(0)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="border-t border-[#E5E7EB] dark:border-slate-800 pt-4 flex justify-between items-center">
                                    <span className="font-medium text-slate-600 dark:text-slate-400">Total Price</span>
                                    <span className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">₹{selectedOrder.total_amount}</span>
                                </div>

                                {selectedOrder.special_instructions && (
                                    <div className="bg-[#FFFBEB] dark:bg-amber-900/10 border border-[#FCD34D] dark:border-amber-500/30 p-3 rounded-xl">
                                        <h4 className="text-xs font-bold text-amber-800 dark:text-amber-500 uppercase tracking-wider mb-1">Customer Note</h4>
                                        <p className="text-sm text-amber-900 dark:text-amber-400">{selectedOrder.special_instructions}</p>
                                    </div>
                                )}

                                {/* Timeline */}
                                 <div>
                                    <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-3">Order Timeline</h3>
                                    <div className="space-y-4 relative pl-4 border-l-2 border-slate-100 dark:border-slate-800 ml-1">
                                        <div className="relative">
                                            <div className="absolute w-3 h-3 bg-emerald-500 rounded-full -left-[9px] top-1 border-2 border-white dark:border-slate-900 shadow-sm"></div>
                                            <p className="text-xs font-bold text-slate-900 dark:text-white">Order Received</p>
                                            <p className="text-[11px] text-slate-500">{new Date(selectedOrder.created_at).toLocaleTimeString()}</p>
                                        </div>
                                        {['preparing', 'ready', 'completed'].includes(selectedOrder.status) && (
                                            <div className="relative">
                                                <div className="absolute w-3 h-3 bg-emerald-500 rounded-full -left-[9px] top-1 border-2 border-white dark:border-slate-900 shadow-sm"></div>
                                                <p className="text-xs font-bold text-slate-900 dark:text-white">Started Preparing</p>
                                            </div>
                                        )}
                                        {['ready', 'completed'].includes(selectedOrder.status) && (
                                            <div className="relative">
                                                <div className="absolute w-3 h-3 bg-emerald-500 rounded-full -left-[9px] top-1 border-2 border-white dark:border-slate-900 shadow-sm"></div>
                                                <p className="text-xs font-bold text-slate-900 dark:text-white">Order Ready</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Right Panel Actions */}
                            <div className="p-5 border-t border-[#E5E7EB] dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 shrink-0 space-y-3">
                                {selectedOrder.status === 'pending' && (
                                    <button 
                                        onClick={() => updateOrderStatus(selectedOrder.id, 'preparing')}
                                        className="w-full h-[44px] bg-[#16A34A] hover:bg-[#15803d] text-white font-semibold rounded-[10px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#16A34A] focus-visible:ring-offset-2 active:scale-95"
                                    >
                                        Start Preparing
                                    </button>
                                )}
                                {selectedOrder.status === 'preparing' && (
                                    <button 
                                        onClick={() => updateOrderStatus(selectedOrder.id, 'ready')}
                                        className="w-full h-[44px] bg-[#16A34A] hover:bg-[#15803d] text-white font-semibold rounded-[10px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#16A34A] focus-visible:ring-offset-2 active:scale-95"
                                    >
                                        Mark as Ready
                                    </button>
                                )}
                                {selectedOrder.status === 'ready' && (
                                    <button 
                                        onClick={() => updateOrderStatus(selectedOrder.id, 'completed')}
                                        className="w-full h-[44px] bg-slate-900 dark:bg-slate-800 hover:bg-slate-800 text-white font-semibold rounded-[10px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 active:scale-95"
                                    >
                                        Mark as Served
                                    </button>
                                )}
                                <div className="flex gap-3">
                                    <button 
                                        onClick={() => setPrintingOrder(selectedOrder)}
                                        className="flex-1 h-10 bg-[#E5E7EB] dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium rounded-lg text-sm transition-colors hover:bg-slate-300 dark:hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 active:scale-95"
                                    >
                                        Print Receipt
                                    </button>
                                    {['pending', 'preparing'].includes(selectedOrder.status) && (
                                        <button 
                                            onClick={() => { updateOrderStatus(selectedOrder.id, 'cancelled'); setSelectedOrder(null); }}
                                            className="flex-1 h-10 border border-[#FEE2E2] dark:border-red-900/50 text-red-600 dark:text-red-400 font-medium rounded-lg text-sm transition-colors hover:bg-red-50 dark:hover:bg-red-900/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 active:scale-95"
                                        >
                                            Reject
                                        </button>
                                    )}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-slate-400 no-print xl:flex hidden">
                            <span className="material-symbols-outlined text-5xl mb-4 opacity-50">receipt_long</span>
                            <h3 className="font-bold text-slate-600 dark:text-slate-300 mb-2">No Order Selected</h3>
                            <p className="text-sm">Click on any order card to view its full details, items, and timeline here.</p>
                        </div>
                    )}
                </div>

                {/* Overlay for mobile/tablet when panel is open */}
                {selectedOrder && (
                    <button 
                        className="fixed inset-0 bg-slate-900/40 z-40 xl:hidden transition-opacity backdrop-blur-[2px]"
                        onClick={() => setSelectedOrder(null)}
                        aria-label="Close details"
                    />
                )}
            </div>

            {/* Hidden Receipt Print Area */}
            {printingOrder && (
                <div className="hidden print:block print:w-full print:p-0 print:m-0">
                    <PrintableReceipt order={printingOrder} restaurant={restaurant} />
                </div>
            )}
        </MainLayout>
    );
}
